/**
 * Home (marketing landing) module.
 * Designer-provided homepage, wired to real routes and to live pricing from
 * GET /public/plans (via usePublicPlans). Static copy/animations are kept as
 * delivered; only navigation and the Pricing section's data source are wired.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Zap,
  Bot,
  BarChart3,
  Globe,
  ChevronDown,
  ArrowRight,
  Check,
  Star,
  Users,
  TrendingUp,
  Clock,
  Megaphone,
  CreditCard,
  MousePointer2,
  Send,
  Workflow,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { usePublicPlans } from './usePublicPlans';
import type { PublicPlan } from '@/services/public.api';
import { ThemeCtx, useTheme, useThemeState, THEMES, NavBar, Footer } from './marketing-shell';
import { formatTokensCompact } from '@/utils/formatTokens.util';

/* ── data ── */
const BROADCAST_DATA = [
  { day: 'Mon', sent: 12400, opened: 11200, clicked: 4800 },
  { day: 'Tue', sent: 18600, opened: 17100, clicked: 7200 },
  { day: 'Wed', sent: 14200, opened: 13100, clicked: 5600 },
  { day: 'Thu', sent: 22800, opened: 21400, clicked: 9800 },
  { day: 'Fri', sent: 31200, opened: 29600, clicked: 14200 },
  { day: 'Sat', sent: 19400, opened: 18200, clicked: 8100 },
  { day: 'Sun', sent: 15800, opened: 14900, clicked: 6200 },
];

const GROWTH_DATA = [
  { month: 'Jan', revenue: 42000 },
  { month: 'Feb', revenue: 58000 },
  { month: 'Mar', revenue: 71000 },
  { month: 'Apr', revenue: 89000 },
  { month: 'May', revenue: 112000 },
  { month: 'Jun', revenue: 148000 },
  { month: 'Jul', revenue: 198000 },
];

const STATS_HERO = [
  { value: '<3 sec', label: 'AI RESPONSE' },
  { value: '24/7', label: 'AVAILABILITY' },
  { value: '<10 min', label: 'SETUP TIME' },
  { value: '5X', label: 'LEAD CONVERSIONS' },
];

const WHY_WA = [
  { icon: TrendingUp, value: '98%', label: 'Read Rate', sub: 'vs 20% for email' },
  { icon: Users, value: '3×', label: 'More Conversions', sub: 'vs SMS' },
  { icon: Globe, value: '2.7B', label: 'Total Users', sub: 'globally on WhatsApp' },
  { icon: Zap, value: '40s', label: 'Response', sub: 'avg customer reply' },
];

const FEATURES = [
  {
    icon: Megaphone,
    title: 'Broadcast Marketing',
    desc: 'Send promotional messages to unlimited contacts with 8+ message categories and scheduled campaigns.',
    tag: 'Broadcast',
  },
  {
    icon: Workflow,
    title: 'WhatsApp Automation',
    desc: 'Build powerful drip flows with a visual drag-and-drop builder. Automate responses 24/7 without code.',
    tag: 'Automation',
  },
  {
    icon: Bot,
    title: 'AI Chatbots',
    desc: 'Deploy GPT-powered agents that handle FAQs, qualify leads, and close deals automatically.',
    tag: 'AI',
  },
  {
    icon: MousePointer2,
    title: 'Click-to-WA Ads',
    desc: 'Run Facebook & Instagram ads that open WhatsApp conversations directly. Capture leads instantly.',
    tag: 'Ads',
  },
  {
    icon: CreditCard,
    title: 'WhatsApp Payments',
    desc: 'Accept payments inside WhatsApp. Share payment links, invoices, and collect money seamlessly.',
    tag: 'Payments',
  },
  {
    icon: BarChart3,
    title: 'Advanced Analytics',
    desc: 'Track delivery, read rates, campaign ROI, and agent performance with real-time dashboards.',
    tag: 'Analytics',
  },
];

const TABS = [
  { id: 'broadcast', label: 'Broadcast', icon: Send },
  { id: 'automation', label: 'Automation', icon: Workflow },
  { id: 'ai', label: 'AI Chatbot', icon: Bot },
  { id: 'ads', label: 'ClickToWA Ads', icon: MousePointer2 },
];

const TAB_CONTENT: Record<string, { heading: string; sub: string; points: string[] }> = {
  broadcast: {
    heading: 'Broadcast Marketing Messages on WhatsApp (Officially)',
    sub: 'Enjoy a limitless broadcasting experience with the official WhatsApp Business API.',
    points: [
      '8+ Powerful Messaging Categories',
      'Add CTAs — Drive 3× Conversions',
      'Schedule broadcasts 2 months ahead',
      'Send to unlimited contacts at once',
    ],
  },
  automation: {
    heading: 'Automate Your Entire Customer Journey',
    sub: 'Build intelligent workflows that convert prospects into buyers around the clock.',
    points: [
      'Visual drag-and-drop flow builder',
      'Trigger-based automated responses',
      'Multi-step drip sequences',
      'Seamless CRM integrations',
    ],
  },
  ai: {
    heading: 'Deploy GPT-Powered AI Agents in Minutes',
    sub: 'Let AI handle FAQs, qualify leads, and escalate complex queries to human agents.',
    points: [
      'Train on your own business data',
      'Multilingual support (50+ languages)',
      'Smart handoff to live agents',
      '99.9% uptime SLA guaranteed',
    ],
  },
  ads: {
    heading: 'Turn Ads Into WhatsApp Conversations',
    sub: 'Drive high-intent traffic directly into WhatsApp for instant lead capture.',
    points: [
      'Facebook & Instagram ad integration',
      'Instant lead capture, zero friction',
      'Auto-reply sequences post-click',
      'Cost-per-lead up to 70% lower',
    ],
  },
};

const TESTIMONIALS = [
  {
    name: 'Mr. Sandeep',
    role: 'Founder & Director, The Global Rep',
    quote:
      'We represent hotels, DMCs, and airlines across the UAE, India, and CIS markets, and partner communication used to mean endless email threads across time zones. WhatsApp Business lets us share rate updates and close representation deals in a single chat — our response time to trade partners dropped from days to minutes.',
    rating: 5,
    avatar: 'MS',
    color: '#25D366',
  },
  {
    name: 'Mr. Ranjit',
    role: 'Founder, Wrapify',
    quote:
      'Our corporate clients order custom gift hampers and welcome kits in bulk, and we used to go back and forth over calls and email just to confirm specs. Now we share catalogs and confirm every order on WhatsApp — order cycle time is down 60% and repeat corporate gifting orders have doubled.',
    rating: 5,
    avatar: 'MR',
    color: '#128C7E',
  },
  {
    name: 'Mr. Ashish',
    role: 'Founder, MyVisaScore',
    quote:
      'Thousands of applicants run our free visa risk check every month, and they used to email us for their report and chase us with follow-up questions about funds, ties, and travel history. Now we deliver instant risk reports and answer every query straight on WhatsApp — response time is down from hours to seconds, and report unlocks are up 3x.',
    rating: 5,
    avatar: 'MA',
    color: '#075E54',
  },
];

const FAQS = [
  {
    q: 'What is WhatsApp and how does it work?',
    a: 'WhatsApp is an official WhatsApp Business API platform that lets you broadcast messages, build chatbots, automate customer journeys, and accept payments — all within WhatsApp. Connect your WhatsApp Business number to our platform and start engaging customers at scale.',
  },
  {
    q: 'Is WhatsApp an official WhatsApp partner?',
    a: "Yes! WhatsApp is an official Meta Business Partner and WhatsApp BSP (Business Solution Provider). We are recipients of the 'CTX Growth Champion Year 2025' award by WhatsApp, recognizing our impact on business growth.",
  },
  {
    q: 'How many contacts can I broadcast to at once?',
    a: "With WhatsApp's official WhatsApp Business API, you can broadcast to unlimited contacts. There are no per-message limits based on list size — only Meta messaging tier limits apply, which scale up automatically as your quality rating improves.",
  },
  {
    q: 'Can I try WhatsApp for free?',
    a: "Absolutely. We offer a full-featured 14-day free trial with no credit card required. You get access to broadcasting, automation, and analytics. Upgrade to a paid plan when you're ready to scale.",
  },
  {
    q: 'How quickly can I get started?',
    a: "Most businesses go live within 10 minutes. Sign up, connect your WhatsApp number (or we help you get one), import contacts, and send your first broadcast — it's that fast.",
  },
];

/* ──────────────────────────── components ──────────────────────────── */

/* ── floating chat bubbles data ── */
// const FLOAT_BUBBLES = [
//   { msg: 'Order confirmed! 🎉', time: '9:41 AM', side: 'in', x: '6%', y: '18%', delay: '0s', dur: '7s' },
//   { msg: 'Your cart is waiting 🛒', time: '10:02 AM', side: 'out', x: '78%', y: '12%', delay: '1.2s', dur: '8s' },
//   { msg: '98% open rate achieved ✅', time: '11:15 AM', side: 'in', x: '4%', y: '62%', delay: '2.1s', dur: '6.5s' },
//   { msg: 'New lead captured 🔥', time: '2:30 PM', side: 'out', x: '74%', y: '58%', delay: '0.7s', dur: '9s' },
//   { msg: 'Payment received ₹4,299 💰', time: '3:45 PM', side: 'in', x: '8%', y: '82%', delay: '3s', dur: '7.5s' },
//   { msg: 'Broadcast sent to 50K 🚀', time: '4:10 PM', side: 'out', x: '82%', y: '72%', delay: '1.8s', dur: '8.5s' },
// ];

const FLOAT_BUBBLES = [
  // Authentication - A shield feels more enterprise/secure than a standard padlock
  {
    msg: '🛡️ Auth Code: *123456*',
    time: '3:39 PM',
    side: 'in',
    x: '6%',
    y: '18%',
    delay: '0s',
    dur: '7s',
  },

  // Marketing - Sparkles are the universal modern SaaS/AI icon for something new or a premium offer
  {
    msg: '✨ 30% Off Maldives!',
    time: '10:02 AM',
    side: 'out',
    x: '78%',
    y: '12%',
    delay: '1.2s',
    dur: '8s',
  },

  // Utility - The taking-off plane is more dynamic and minimalistic than the standard airplane
  {
    msg: '🛫 Flight 6E-432 on time',
    time: '4:15 PM',
    side: 'in',
    x: '4%',
    y: '62%',
    delay: '2.1s',
    dur: '6.5s',
  },

  // Marketing - A shopping bag is universally used in high-end D2C/E-commerce instead of a grocery cart
  {
    msg: '🛍️ Paris tour saved',
    time: '2:30 PM',
    side: 'out',
    x: '74%',
    y: '58%',
    delay: '0.7s',
    dur: '9s',
  },

  // Utility - A concierge bell represents premium hospitality much better than a generic building block
  {
    msg: '🛎️ Booking Confirmed',
    time: '11:45 AM',
    side: 'in',
    x: '8%',
    y: '82%',
    delay: '3s',
    dur: '7.5s',
  },

  // Utility / Auth - A mobile ping feels more like a modern 2FA alert
  {
    msg: '📲 New login: Noida',
    time: '9:10 AM',
    side: 'out',
    x: '82%',
    y: '72%',
    delay: '1.8s',
    dur: '8.5s',
  },
];

const ROTATE_WORDS = ['Broadcast', 'Automate', 'Engage', 'Sell', 'Convert', 'Grow'];

function HeroSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const navigate = useNavigate();
  const [wordIdx, setWordIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const [msgCount, setMsgCount] = useState(2847391);

  /* rotate verb */
  useEffect(() => {
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setWordIdx((i) => (i + 1) % ROTATE_WORDS.length);
        setFading(false);
      }, 350);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  /* live message counter */
  useEffect(() => {
    const id = setInterval(() => setMsgCount((n) => n + Math.floor(Math.random() * 7 + 3)), 800);
    return () => clearInterval(id);
  }, []);

  const isDark = theme === 'dark';
  const goSignup = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/signup');
  };

  return (
    <section
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        paddingTop: 90,
      }}
    >
      {/* ── full-bleed background ── */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isDark
            ? 'radial-gradient(ellipse 120% 80% at 50% 60%, rgba(61,220,132,0.07) 0%, transparent 60%), #0D1117'
            : 'radial-gradient(ellipse 120% 80% at 50% 60%, rgba(37,211,102,0.10) 0%, transparent 60%), #F0FFF6',
          pointerEvents: 'none',
        }}
      />

      {/* dot grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: `radial-gradient(circle, ${isDark ? 'rgba(61,220,132,0.18)' : 'rgba(37,211,102,0.22)'} 1px, transparent 1px)`,
          backgroundSize: '36px 36px',
          opacity: isDark ? 0.5 : 0.6,
          maskImage: 'radial-gradient(ellipse 90% 80% at 50% 50%, black 30%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 90% 80% at 50% 50%, black 30%, transparent 100%)',
        }}
      />

      {/* ── floating WhatsApp bubbles — purely decorative; hidden on mobile via
           .hero-bubbles below, since their fixed x/y % positions overlap the
           headline once the viewport is too narrow to give them room ── */}
      <div className="hero-bubbles">
        {FLOAT_BUBBLES.map((b, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: b.x,
              top: b.y,
              pointerEvents: 'none',
              animation: `heroBubble ${b.dur} ease-in-out ${b.delay} infinite`,
              opacity: 0.75,
              zIndex: 1,
            }}
          >
            <div
              style={{
                padding: '9px 14px',
                borderRadius: b.side === 'in' ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
                background:
                  b.side === 'in'
                    ? isDark
                      ? 'rgba(255,255,255,0.07)'
                      : 'rgba(255,255,255,0.9)'
                    : isDark
                      ? 'rgba(61,220,132,0.18)'
                      : 'rgba(37,211,102,0.18)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(37,211,102,0.3)'}`,
                backdropFilter: 'blur(12px)',
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.08)',
                maxWidth: 200,
                whiteSpace: 'nowrap',
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: isDark ? '#E6EDF3' : '#0A1A0F',
                  letterSpacing: '0.01em',
                }}
              >
                {b.msg}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                  marginTop: 3,
                  textAlign: 'right',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 3,
                }}
              >
                {b.time}{' '}
                {b.side === 'out' && <span style={{ color: t.green, fontSize: 11 }}>✓✓</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── center content ── */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: 900,
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {/* live counter pill */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 20px',
            borderRadius: 50,
            background: isDark ? 'rgba(61,220,132,0.09)' : 'rgba(37,211,102,0.12)',
            border: `1px solid ${isDark ? 'rgba(61,220,132,0.25)' : 'rgba(37,211,102,0.35)'}`,
            marginBottom: 36,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: t.green,
              display: 'inline-block',
              animation: 'pulse 1.2s ease-in-out infinite',
              boxShadow: `0 0 8px ${t.green}`,
            }}
          />
          <span
            style={{
              fontSize: 13,
              color: t.green,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            {msgCount.toLocaleString()}
          </span>
          <span style={{ fontSize: 13, color: t.mutedFg, fontWeight: 500 }}>
            messages sent today
          </span>
        </div>

        {/* giant headline */}
        <h1
          style={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 800,
            lineHeight: 1.0,
            letterSpacing: '-3px',
            marginBottom: 0,
            fontSize: 'clamp(3.2rem, 9vw, 7.5rem)',
            color: t.fg,
          }}
        >
          <span style={{ display: 'block' }}>
            <span
              style={{
                color: t.green,
                display: 'inline-block',
                opacity: fading ? 0 : 1,
                transform: fading ? 'translateY(-12px)' : 'translateY(0)',
                transition: 'opacity 0.35s ease, transform 0.35s ease',
                minWidth: '5ch',
              }}
            >
              {ROTATE_WORDS[wordIdx]}
            </span>
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 'clamp(2rem, 5.5vw, 4.8rem)',
              color: isDark ? '#CDD9E5' : '#1A3A22',
              letterSpacing: '-2px',
              marginTop: 4,
            }}
          >
            on WhatsApp.
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 'clamp(1.4rem, 3.2vw, 2.8rem)',
              color: t.mutedFg,
              fontWeight: 600,
              letterSpacing: '-0.5px',
              marginTop: 8,
              lineHeight: 1.3,
            }}
          >
            Turn every message into revenue.
          </span>
        </h1>

        {/* sub text */}
        <p
          style={{
            fontSize: 'clamp(15px, 2vw, 17px)',
            color: t.mutedFg,
            lineHeight: 1.75,
            maxWidth: 520,
            margin: '32px 0 40px',
          }}
        >
          Smart automation. Real conversations. The WhatsApp growth platform powered by{' '}
          <strong style={{ color: t.fg }}>AI</strong> that actually sounds like you.
        </p>

        {/* CTAs */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            justifyContent: 'center',
            marginBottom: 56,
          }}
        >
          <a
            href="/signup"
            onClick={goSignup}
            style={{
              padding: '16px 34px',
              borderRadius: 14,
              background: t.green,
              color: isDark ? '#050C09' : '#fff',
              fontWeight: 800,
              fontSize: 15,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: 'Manrope, sans-serif',
              boxShadow: `0 8px 40px ${isDark ? 'rgba(61,220,132,0.4)' : 'rgba(37,211,102,0.5)'}`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
              (e.currentTarget as HTMLAnchorElement).style.boxShadow =
                `0 14px 50px ${isDark ? 'rgba(61,220,132,0.55)' : 'rgba(37,211,102,0.65)'}`;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
              (e.currentTarget as HTMLAnchorElement).style.boxShadow =
                `0 8px 40px ${isDark ? 'rgba(61,220,132,0.4)' : 'rgba(37,211,102,0.5)'}`;
            }}
          >
            Start Free — No Card Needed <ArrowRight size={16} />
          </a>
          {/* <a 
            href="#"
            style={{
              padding: '16px 28px',
              borderRadius: 14,
              border: `1.5px solid ${t.border}`,
              color: t.fg,
              fontWeight: 600,
              fontSize: 15,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: t.greenDim,
              transition: 'border-color 0.2s, background 0.2s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.borderColor = t.green;
              (e.currentTarget as HTMLAnchorElement).style.background = t.greenDim2;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.borderColor = t.border;
              (e.currentTarget as HTMLAnchorElement).style.background = t.greenDim;
            }}
          >
            <Play size={14} style={{ fill: 'currentColor' }} /> Watch 2-min Demo
          </a>*/}
        </div>

        {/* stat strip */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 0,
            borderRadius: 18,
            overflow: 'hidden',
            border: `1px solid ${t.border}`,
            background: t.card,
          }}
        >
          {STATS_HERO.map(({ value, label }, i) => (
            <div
              key={label}
              style={{
                padding: '18px 32px',
                textAlign: 'center',
                borderRight: i < STATS_HERO.length - 1 ? `1px solid ${t.border}` : 'none',
              }}
            >
              <div
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 800,
                  fontSize: 28,
                  color: t.green,
                  letterSpacing: '-1px',
                }}
              >
                {value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: t.mutedFg,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginTop: 2,
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* social proof avatars */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 28 }}>
          <div style={{ display: 'flex' }}>
            {['#3DDC84', '#25D366', '#128C7E', '#075E54', '#34B7F1'].map((c, i) => (
              <div
                key={i}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: c,
                  border: `2px solid ${t.bg}`,
                  marginLeft: i === 0 ? 0 : -10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#fff',
                }}
              >
                {['A', 'B', 'C', 'D', 'E'][i]}
              </div>
            ))}
          </div>
          <div>
            <div style={{ display: 'flex', gap: 2 }}>
              {[1, 2, 3, 4, 5].map((s) => (
                <span key={s} style={{ color: '#F5A623', fontSize: 13 }}>
                  ★
                </span>
              ))}
            </div>
            <div style={{ fontSize: 12, color: t.mutedFg, marginTop: 1 }}>
              AI-native platform for <strong style={{ color: t.fg }}>5X</strong> faster growth
            </div>
          </div>
        </div>
      </div>

      {/* ── bottom fade ── */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 120,
          background: `linear-gradient(to top, ${isDark ? '#0D1117' : '#F0FFF6'}, transparent)`,
          pointerEvents: 'none',
        }}
      />

      <style>{`
        @media (max-width: 768px) { .hero-bubbles { display: none; } }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.85); } }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        @keyframes heroBubble { 0%, 100% { transform: translateY(0px) rotate(-1deg); } 50% { transform: translateY(-14px) rotate(1deg); } }
        @keyframes photoStrip { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(37,211,102,0.3); border-radius: 4px; }
      `}</style>
    </section>
  );
}

function WhyWhatsAppSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];

  return (
    <section style={{ padding: '80px 24px', background: t.bg }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: 50,
              background: t.greenDim,
              border: `1px solid ${t.border}`,
              fontSize: 11,
              color: t.green,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 20,
            }}
          >
            Why WhatsApp?
          </span>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(2rem, 4vw, 3.2rem)',
              fontWeight: 800,
              color: t.fg,
              marginBottom: 14,
              letterSpacing: '-0.5px',
            }}
          >
            The Channel That Actually Works
          </h2>
          <p style={{ color: t.mutedFg, fontSize: 17, maxWidth: 500, margin: '0 auto' }}>
            WhatsApp outperforms every other marketing channel — by a massive margin.
          </p>
        </div>

        {/* Stats cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
            marginBottom: 60,
          }}
        >
          {WHY_WA.map(({ icon: Icon, value, label, sub }) => (
            <div
              key={label}
              style={{
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: 20,
                padding: '28px 24px',
                position: 'relative',
                overflow: 'hidden',
                transition: 'border-color 0.3s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = t.borderHover)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = t.border)}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 120,
                  height: 120,
                  background: `radial-gradient(circle at 100% 0%, ${t.greenDim}, transparent 70%)`,
                  borderRadius: '0 20px 0 0',
                }}
              />
              <Icon size={22} color={t.green} style={{ marginBottom: 14 }} />
              <div
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 800,
                  fontSize: 40,
                  color: t.green,
                  lineHeight: 1,
                }}
              >
                {value}
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: t.fg,
                  fontSize: 16,
                  marginTop: 6,
                  marginBottom: 4,
                }}
              >
                {label}
              </div>
              <div style={{ color: t.mutedFg, fontSize: 13 }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Growth chart */}
        <div
          style={{
            background: t.card,
            border: `1px solid ${t.border}`,
            borderRadius: 24,
            padding: '32px 28px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 24,
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 800,
                  fontSize: 20,
                  color: t.fg,
                }}
              >
                Revenue Growth with WhatsApp
              </div>
              <div style={{ color: t.mutedFg, fontSize: 13, marginTop: 4 }}>
                Average customer revenue trajectory over 7 months
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: t.greenDim,
                border: `1px solid ${t.border}`,
                borderRadius: 10,
                padding: '6px 14px',
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: t.green }} />
              <span style={{ fontSize: 12, color: t.green, fontWeight: 700 }}>
                +371% avg growth
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={GROWTH_DATA} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.divider} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: t.mutedFg }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: t.mutedFg }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`}
              />
              <Tooltip
                formatter={(v: number) => [`₹${v.toLocaleString()}`, 'Revenue']}
                contentStyle={{
                  background: t.card,
                  border: `1px solid ${t.border}`,
                  borderRadius: 10,
                  fontSize: 12,
                }}
                labelStyle={{ color: t.fg }}
                itemStyle={{ color: t.green }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke={t.chartStroke}
                strokeWidth={2.5}
                dot={{ fill: t.chartStroke, strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

/* WhatsApp phone mockup */
function PhoneMockup({ tabId }: { tabId: string }) {
  const { theme } = useTheme();
  const t = THEMES[theme];

  const messages: Record<string, { from: 'biz' | 'user'; text: string; extra?: string }[]> = {
    broadcast: [
      {
        from: 'biz',
        text: '🎉 Exclusive Weekend Sale!',
        extra: 'Get 40% OFF — valid till Sunday midnight. Use code *BAATLYNEW*',
      },
      { from: 'user', text: 'Interested! What products are on sale?' },
      { from: 'biz', text: 'All our premium plans are on sale. Tap below 👇' },
    ],
    automation: [
      { from: 'biz', text: "Hi! I'm your WhatsApp AI assistant 🤖 How can I help?" },
      { from: 'user', text: 'What plans do you offer?' },
      {
        from: 'biz',
        text: 'We have Starter, Growth & Enterprise plans. Want me to share pricing?',
      },
    ],
    ai: [
      { from: 'user', text: 'I need help resetting my account password' },
      { from: 'biz', text: "Sure! I'll send a reset link to your email in 30 seconds ✅" },
      { from: 'biz', text: 'Done! Check your inbox. Anything else I can help with?' },
    ],
    ads: [
      { from: 'biz', text: '👋 Welcome! You clicked our ad — great timing!' },
      { from: 'biz', text: 'Get started with WhatsApp FREE for 14 days. No credit card needed.' },
      { from: 'user', text: 'Yes, I want to try it!' },
    ],
  };

  const msgs = messages[tabId] ?? messages['broadcast'] ?? [];

  return (
    <div style={{ position: 'relative', width: 280, margin: '0 auto' }}>
      {/* Glow */}
      <div
        style={{
          position: 'absolute',
          bottom: -30,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 200,
          height: 40,
          background: `radial-gradient(ellipse, ${t.green} 0%, transparent 70%)`,
          filter: 'blur(20px)',
          opacity: 0.3,
        }}
      />

      <div
        style={{
          background: t.card,
          border: `1px solid ${t.border}`,
          borderRadius: 36,
          overflow: 'hidden',
          boxShadow: t.shadow,
        }}
      >
        {/* Status bar */}
        <div
          style={{
            background: t.card2,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 20px',
          }}
        >
          <span style={{ fontSize: 10, color: t.mutedFg, fontWeight: 600 }}>9:41</span>
          <div style={{ width: 60, height: 4, borderRadius: 4, background: t.muted }} />
          <span style={{ fontSize: 10, color: t.mutedFg }}>●●●</span>
        </div>
        {/* Chat header */}
        <div
          style={{
            background: theme === 'dark' ? '#075E54' : '#128C7E',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#25D366',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 800,
              color: '#fff',
            }}
          >
            A
          </div>
          <div>
            <div style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>WhatsApp Business</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>● Online</div>
          </div>
        </div>
        {/* Messages */}
        <div
          style={{
            background: theme === 'dark' ? '#0B141A' : '#ECE5DD',
            padding: '14px 12px',
            minHeight: 280,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {msgs.map((m, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: m.from === 'biz' ? 'flex-start' : 'flex-end',
              }}
            >
              <div
                style={{
                  maxWidth: '80%',
                  background:
                    m.from === 'biz'
                      ? theme === 'dark'
                        ? '#1F2C34'
                        : '#fff'
                      : theme === 'dark'
                        ? '#005C4B'
                        : '#DCF8C6',
                  borderRadius: m.from === 'biz' ? '0 14px 14px 14px' : '14px 0 14px 14px',
                  padding: '8px 12px',
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    color:
                      m.from === 'biz'
                        ? theme === 'dark'
                          ? '#E9EDEF'
                          : '#111'
                        : theme === 'dark'
                          ? '#E9EDEF'
                          : '#111',
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {m.text}
                </p>
                {m.extra && (
                  <p
                    style={{
                      fontSize: 10,
                      color: theme === 'dark' ? '#8696A0' : '#555',
                      margin: '4px 0 0',
                      lineHeight: 1.4,
                    }}
                  >
                    {m.extra}
                  </p>
                )}
                {m.from === 'biz' && m === msgs[0] && (
                  <div
                    style={{
                      marginTop: 8,
                      background: theme === 'dark' ? '#005C4B' : '#25D366',
                      borderRadius: 8,
                      padding: '5px 10px',
                      textAlign: 'center',
                    }}
                  >
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>
                      Claim Offer →
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {/* Input bar */}
        <div
          style={{
            background: t.card2,
            padding: '10px 12px',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              flex: 1,
              background: t.muted,
              borderRadius: 20,
              padding: '7px 14px',
              fontSize: 11,
              color: t.mutedFg,
            }}
          >
            Type a message...
          </div>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: t.green,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Send size={13} color="#fff" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Full platform capabilities (grouped feature list) ──────────────────────
const PLATFORM_FEATURE_GROUPS: Array<{
  emoji: string;
  title: string;
  subtitle?: string;
  points: string[];
}> = [
  {
    emoji: '💬',
    title: 'Message Routing',
    subtitle: 'Smart Inbound WhatsApp',
    points: [
      'Always-on AI agent — replies to every incoming WhatsApp message instantly, 24/7',
      'Answers from your knowledge base — trained on your own docs, FAQs & Q&A (RAG)',
      'Best-agent routing — automatically picks the right AI persona per message',
      'Keyword & button-tap flows — instant scripted replies for known questions, zero AI cost',
      'AI Answer node — hand a specific topic to a chosen expert agent',
      'Approved-template quick replies — fully WhatsApp/Meta compliant',
      'Automatic human escalation — on low confidence or trigger phrases',
      'Guaranteed handoff — bot steps back, your team takes over',
      'Business-hours aware + STOP/START opt-out compliant',
      'Visual drag-and-drop builder with a live "test a message" preview',
      'Starter templates — general + industry packs (e.g. Travel)',
    ],
  },
  {
    emoji: '🔁',
    title: 'CRM Automation',
    subtitle: 'Lead Workflows',
    points: [
      'Event-triggered workflows — fire on new leads and lead activity',
      'Auto-assign leads to the right agent or consultant',
      'Smart branching — route by lead score, source, status & more',
      'Time delays for multi-step nurture sequences',
      'Send campaigns inside a workflow — the message they receive',
      'Auto-update lead fields as they move through the funnel',
      'Chain workflows & call external systems via webhooks',
      'Full execution history with per-step activity tracking',
      'Ready-made routing templates',
    ],
  },
  {
    emoji: '📣',
    title: 'Campaigns',
    subtitle: 'Outbound WhatsApp',
    points: [
      'Bulk campaigns to segments, tags & saved audiences',
      'Personalized approved templates (variable substitution)',
      'Flexible scheduling — send now, scheduled, or recurring',
      'Recurring controls — end date, max runs, timezone',
      'Behavior triggers — inactivity & follow-up campaigns',
      'Automatic retries on failed sends (configurable rounds & delays)',
      'Safety caps — max recipients + daily send limit per campaign',
      'Smart throttling to respect WhatsApp rate limits',
      'Live delivery status + audience reach estimates',
      'Duplicate-send protection',
    ],
  },
  {
    emoji: '🔌',
    title: 'Developer API & Integrations',
    points: [
      'REST API to send WhatsApp messages programmatically',
      'Trigger campaigns, manage contacts & leads via API',
      'Scoped API keys — per-integration permissions, create / rotate / revoke',
      'Signed webhook callbacks (HMAC) for delivery & status updates',
      'Per-key rate limits + brute-force lockout',
      'Bulk contact/lead upsert',
      'CRM lead webhooks — real-time lead sync from Meta Lead Ads & HubSpot',
      'In-app API reference',
    ],
  },
  {
    emoji: '🤖',
    title: 'AI Agents & Knowledge Base',
    points: [
      'Multiple AI agents — distinct personas & specializations',
      'Per-agent knowledge base from files, text & Q&A pairs',
      'Dynamic agent selection or pin a default agent',
      'Configurable model, temperature & response length (tenant + per-agent)',
    ],
  },
  {
    emoji: '👥',
    title: 'Team & Human Handoff',
    points: [
      'Shared human inbox with conversation ownership',
      'Round-robin assignment',
      'Escalation rules — keywords + low-confidence auto-handoff',
      'Chat logs with unread & assigned tracking',
    ],
  },
  {
    emoji: '🔒',
    title: 'Trust, Security & Reliability',
    points: [
      'Dedicated database per customer — full tenant isolation',
      'Secrets encrypted at rest (AES-256-GCM); passwords hashed (bcrypt)',
      'Official WhatsApp Business API via Meta — billed at 0% markup',
      'HMAC-signed API & webhook callbacks',
      'Scoped API keys — rotation, instant revoke & brute-force lockout',
      'Duplicate-send protection + automatic retries with a dead-letter safety net',
      'Real-time delivery status & an append-only audit trail',
      'Role-based access, plan entitlements & usage limits',
    ],
  },
];

/** Bold the lead phrase (before the em-dash), muted for the rest. */
function renderFeaturePoint(pt: string, t: any) {
  const i = pt.indexOf(' — ');
  if (i === -1) return <span style={{ color: t.fg }}>{pt}</span>;
  return (
    <>
      <strong style={{ color: t.fg, fontWeight: 700 }}>{pt.slice(0, i)}</strong>
      <span style={{ color: t.mutedFg }}>{pt.slice(i)}</span>
    </>
  );
}

function PlatformFeaturesSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  return (
    <section id="all-features" style={{ padding: '90px 24px', background: t.bg }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: 50,
              background: t.greenDim,
              border: `1px solid ${t.border}`,
              fontSize: 11,
              color: t.green,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 20,
            }}
          >
            The complete platform
          </span>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(2rem, 4vw, 3.2rem)',
              fontWeight: 800,
              color: t.fg,
              marginBottom: 14,
              letterSpacing: '-0.5px',
            }}
          >
            Everything included, out of the box
          </h2>
          <p style={{ color: t.mutedFg, fontSize: 17, maxWidth: 560, margin: '0 auto' }}>
            Smart inbound replies, CRM automation, bulk campaigns, a full developer API — and
            enterprise-grade security under all of it.
          </p>
        </div>

        <div
          style={{
            overflowX: 'auto',
            border: `1px solid ${t.border}`,
            borderRadius: 20,
            background: t.card,
          }}
        >
          <div style={{ minWidth: 680 }}>
            {/* header row */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '230px 1fr',
                borderBottom: `1px solid ${t.border}`,
              }}
            >
              <div
                style={{
                  padding: '16px 24px',
                  fontSize: 11.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: t.mutedFg,
                  borderRight: `1px solid ${t.border}`,
                }}
              >
                Feature area
              </div>
              <div
                style={{
                  padding: '16px 24px',
                  fontSize: 11.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: t.mutedFg,
                }}
              >
                What's included
              </div>
            </div>
            {/* one row per capability group */}
            {PLATFORM_FEATURE_GROUPS.map((g, idx) => (
              <div
                key={g.title}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '230px 1fr',
                  borderTop: idx === 0 ? 'none' : `1px solid ${t.border}`,
                }}
              >
                <div
                  style={{
                    padding: '20px 24px',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    borderRight: `1px solid ${t.border}`,
                  }}
                >
                  <span style={{ fontSize: 22, lineHeight: 1 }}>{g.emoji}</span>
                  <div>
                    <div
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 800,
                        fontSize: 15,
                        color: t.fg,
                        lineHeight: 1.25,
                      }}
                    >
                      {g.title}
                    </div>
                    {g.subtitle && (
                      <div style={{ fontSize: 12, color: t.mutedFg, marginTop: 3 }}>
                        {g.subtitle}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    padding: '18px 24px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '10px 22px',
                  }}
                >
                  {g.points.map((pt) => (
                    <div key={pt} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                      <span
                        style={{
                          flexShrink: 0,
                          width: 17,
                          height: 17,
                          borderRadius: '50%',
                          background: t.greenDim,
                          border: `1px solid ${t.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginTop: 2,
                        }}
                      >
                        <Check size={10} color={t.green} strokeWidth={3} />
                      </span>
                      <span style={{ fontSize: 13, lineHeight: 1.5 }}>
                        {renderFeaturePoint(pt, t)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturesTabSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const navigate = useNavigate();
  const [active, setActive] = useState('broadcast');
  const content = (TAB_CONTENT[active] ?? TAB_CONTENT['broadcast'])!;
  const goSignup = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/signup');
  };

  return (
    <section id="features" style={{ padding: '80px 24px', background: t.card }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: 50,
              background: t.greenDim,
              border: `1px solid ${t.border}`,
              fontSize: 11,
              color: t.green,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 20,
            }}
          >
            Platform Capabilities
          </span>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(2rem, 4vw, 3.2rem)',
              fontWeight: 800,
              color: t.fg,
              marginBottom: 14,
              letterSpacing: '-0.5px',
            }}
          >
            Everything you need to automate
            <br />
            {/* Features that Drive Conversions */}
          </h2>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 48,
          }}
        >
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActive(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: 'none',
                background: active === id ? t.green : t.muted,
                color: active === id ? (theme === 'dark' ? '#06090A' : '#fff') : t.mutedFg,
                boxShadow: active === id ? `0 0 20px ${t.greenGlow}` : 'none',
              }}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Content grid */}
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}
          className="feature-grid"
        >
          <div>
            <h3
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 800,
                fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                color: t.fg,
                marginBottom: 16,
                lineHeight: 1.25,
              }}
            >
              {content.heading}
            </h3>
            <p style={{ color: t.mutedFg, fontSize: 16, marginBottom: 28, lineHeight: 1.7 }}>
              {content.sub}
            </p>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}>
              {content.points.map((pt) => (
                <li
                  key={pt}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, listStyle: 'none' }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: t.greenDim,
                      border: `1px solid ${t.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Check size={12} color={t.green} strokeWidth={3} />
                  </span>
                  <span style={{ color: t.fg, fontSize: 15 }}>{pt}</span>
                </li>
              ))}
            </ul>
            <a
              href="/signup"
              onClick={goSignup}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 24px',
                borderRadius: 12,
                background: t.green,
                color: theme === 'dark' ? '#06090A' : '#fff',
                fontWeight: 700,
                fontSize: 14,
                textDecoration: 'none',
                boxShadow: `0 0 20px ${t.greenGlow}`,
                cursor: 'pointer',
              }}
            >
              Start for FREE <ArrowRight size={15} />
            </a>
          </div>
          <div className="phone-wrapper" style={{ display: 'flex', justifyContent: 'center' }}>
            <PhoneMockup tabId={active} />
          </div>
        </div>
      </div>
      <style>{`.feature-grid { @media (max-width: 900px) { grid-template-columns: 1fr !important; } } .phone-wrapper { @media (max-width: 900px) { display: none !important; } }`}</style>
    </section>
  );
}

function FeaturesGrid() {
  const { theme } = useTheme();
  const t = THEMES[theme];

  return (
    <section style={{ padding: '80px 24px', background: t.bg }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: 50,
              background: t.greenDim,
              border: `1px solid ${t.border}`,
              fontSize: 11,
              color: t.green,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 20,
            }}
          >
            Everything you need
          </span>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(2rem, 4vw, 3.2rem)',
              fontWeight: 800,
              color: t.fg,
              marginBottom: 14,
              letterSpacing: '-0.5px',
            }}
          >
            One Platform. Infinite Growth.
          </h2>
          <p style={{ color: t.mutedFg, fontSize: 17, maxWidth: 500, margin: '0 auto' }}>
            Top-rated WhatsApp Engagement Platform for startups & growing businesses.
          </p>
        </div>

        <div
          className="features-grid-3col"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}
        >
          {FEATURES.map(({ icon: Icon, title, desc, tag }) => (
            <div
              key={title}
              style={{
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: 20,
                padding: '28px',
                transition: 'all 0.3s',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = t.borderHover;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = t.border;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 100,
                  height: 100,
                  background: `radial-gradient(circle at 100% 0%, ${t.greenDim}, transparent 70%)`,
                  borderRadius: '0 20px 0 0',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 18,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: t.greenDim,
                    border: `1px solid ${t.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={20} color={t.green} />
                </div>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: t.mutedFg,
                    background: t.muted,
                    padding: '4px 10px',
                    borderRadius: 20,
                    border: `1px solid ${t.border}`,
                  }}
                >
                  {tag}
                </span>
              </div>
              <h3
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 800,
                  fontSize: 17,
                  color: t.fg,
                  marginBottom: 8,
                }}
              >
                {title}
              </h3>
              <p style={{ color: t.mutedFg, fontSize: 13.5, lineHeight: 1.65 }}>{desc}</p>
              {/* <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  color: t.green,
                  fontSize: 13,
                  fontWeight: 600,
                  marginTop: 16,
                }}
              >
                Learn more <ChevronRight size={14} />
              </div> */}
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 1000px) { .features-grid-3col { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 640px) { .features-grid-3col { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}

function BroadcastAnalyticsSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];

  return (
    <section style={{ padding: '80px 24px', background: t.card }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}
          className="analytics-grid"
        >
          {/* Chart visual */}
          <div
            style={{
              background: t.bg,
              border: `1px solid ${t.border}`,
              borderRadius: 24,
              padding: 28,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 800,
                    fontSize: 17,
                    color: t.fg,
                  }}
                >
                  Weekly Broadcast Stats
                </div>
                <div style={{ fontSize: 12, color: t.mutedFg, marginTop: 2 }}>
                  Sent vs Opened vs Clicked
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14 }}>
                {[
                  { color: t.green, label: 'Opened' },
                  { color: theme === 'dark' ? '#34A853' : '#128C7E', label: 'Clicked' },
                ].map(({ color, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
                    <span style={{ fontSize: 11, color: t.mutedFg }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={BROADCAST_DATA} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={t.divider} vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11, fill: t.mutedFg }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: t.mutedFg }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    background: t.card,
                    border: `1px solid ${t.border}`,
                    borderRadius: 10,
                    fontSize: 12,
                  }}
                  itemStyle={{ color: t.green }}
                  labelStyle={{ color: t.fg }}
                />
                <Bar dataKey="opened" fill={t.chartStroke} radius={[4, 4, 0, 0]} />
                <Bar
                  dataKey="clicked"
                  fill={theme === 'dark' ? '#34A853' : '#128C7E'}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>

            {/* Summary chips */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
                marginTop: 16,
              }}
            >
              {[
                { label: 'Avg Open Rate', val: '93.4%' },
                { label: 'Avg CTR', val: '38.7%' },
                { label: 'Best Day', val: 'Friday' },
              ].map(({ label, val }) => (
                <div
                  key={label}
                  style={{
                    background: t.card,
                    borderRadius: 10,
                    padding: '10px 12px',
                    border: `1px solid ${t.border}`,
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 800,
                      fontSize: 18,
                      color: t.green,
                    }}
                  >
                    {val}
                  </div>
                  <div style={{ fontSize: 10, color: t.mutedFg, marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Text */}
          <div>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                borderRadius: 50,
                background: t.greenDim,
                border: `1px solid ${t.border}`,
                fontSize: 11,
                color: t.green,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 20,
              }}
            >
              Real-time Analytics
            </span>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontSize: 'clamp(1.8rem, 3vw, 2.8rem)',
                fontWeight: 800,
                color: t.fg,
                marginBottom: 18,
                lineHeight: 1.2,
                letterSpacing: '-0.5px',
              }}
            >
              Know Exactly What's Working — Live
            </h2>
            <p style={{ color: t.mutedFg, fontSize: 16, lineHeight: 1.75, marginBottom: 28 }}>
              Track every message, measure every campaign, and optimize in real-time. Get delivery,
              read, click, and conversion data at your fingertips — no more guessing what works.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
              {[
                'Live campaign dashboards with second-by-second updates',
                'A/B test messages and pick winners automatically',
                'Agent performance & team reports',
                'Export data to Google Sheets or your CRM',
              ].map((pt) => (
                <div key={pt} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span
                    style={{
                      flexShrink: 0,
                      marginTop: 2,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: t.greenDim,
                      border: `1px solid ${t.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Check size={11} color={t.green} strokeWidth={3} />
                  </span>
                  <span style={{ color: t.fg, fontSize: 14.5 }}>{pt}</span>
                </div>
              ))}
            </div>
            {/* <a
              href="#"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 24px',
                borderRadius: 12,
                border: `1.5px solid ${t.border}`,
                color: t.fg,
                fontWeight: 700,
                fontSize: 14,
                textDecoration: 'none',
                background: t.greenDim,
              }}
            >
              View Analytics Demo <ChevronRight size={15} />
            </a> */}
          </div>
        </div>
      </div>
      <style>{`.analytics-grid { @media (max-width: 900px) { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function TestimonialsSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];

  return (
    <section style={{ padding: '80px 24px', background: t.bg }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: 50,
              background: t.greenDim,
              border: `1px solid ${t.border}`,
              fontSize: 11,
              color: t.green,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 20,
            }}
          >
            Social Proof
          </span>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(2rem, 4vw, 3.2rem)',
              fontWeight: 800,
              color: t.fg,
              marginBottom: 12,
              letterSpacing: '-0.5px',
            }}
          >
            Founders & Marketers Love Us
          </h2>
          <p style={{ color: t.mutedFg, fontSize: 17 }}>
            Real stories from teams who automate without losing their voice
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 20,
          }}
        >
          {TESTIMONIALS.map(({ name, role, quote, rating, avatar, color }) => (
            <div
              key={name}
              style={{
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: 20,
                padding: 28,
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
                transition: 'border-color 0.3s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = t.borderHover)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = t.border)}
            >
              <div style={{ display: 'flex', gap: 3 }}>
                {Array.from({ length: rating }).map((_, i) => (
                  <Star key={i} size={14} fill="#25D366" color="#25D366" />
                ))}
              </div>
              <blockquote
                style={{
                  color: t.textSecondary,
                  fontSize: 14,
                  lineHeight: 1.75,
                  flex: 1,
                  margin: 0,
                }}
              >
                "{quote}"
              </blockquote>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  paddingTop: 16,
                  borderTop: `1px solid ${t.divider}`,
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: `${color}20`,
                    border: `2px solid ${color}40`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 800,
                    fontSize: 14,
                    color,
                  }}
                >
                  {avatar}
                </div>
                <div>
                  <div style={{ color: t.fg, fontSize: 14, fontWeight: 700 }}>{name}</div>
                  <div style={{ color: t.mutedFg, fontSize: 12 }}>{role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── scroll reveal hook ── */
function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ── use cases photo section ── */
const USE_CASES = [
  {
    label: 'E-commerce',
    title: 'Nudge them back to the checkout',
    desc: 'Send personalised reminders, discount codes and product carousels right inside WhatsApp — recover up to 60% of lost sales.',
    img: 'https://images.unsplash.com/photo-1727407209320-1fa6ae60ee05?w=800&h=600&fit=crop&auto=format',
    alt: 'Woman shopping online with tablet and bags',
    stat: '60% cart recovery',
  },
  {
    label: 'Travel & Hospitality',
    title: 'Be their digital travel companion',
    desc: 'Share instant flight updates, rich itineraries, and smooth hotel check-ins. Give your travelers total peace of mind from takeoff to checkout',
    img: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop&auto=format',
    alt: 'Team collaborating on digital marketing strategy',
    stat: '24/7 Concierge',
  },
  {
    label: 'Finance',
    title: 'Send Instant Policy & Payment Alerts',
    desc: 'Deliver real-time loan updates, EMI reminders and KYC flows on WhatsApp — fully compliant with official WhatsApp Business API.',
    img: 'https://images.unsplash.com/photo-1572016047668-5b5e909e1605?w=800&h=600&fit=crop&auto=format',
    alt: 'Professional using smartphone for business',
    stat: '98% delivery rate',
  },
];

function UseCaseCard({
  label,
  title,
  desc,
  img,
  alt,
  stat,
  index,
}: {
  label: string;
  title: string;
  desc: string;
  img: string;
  alt: string;
  stat: string;
  index: number;
}) {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const { ref, visible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      style={{
        borderRadius: 24,
        overflow: 'hidden',
        background: t.card,
        border: `1px solid ${t.border}`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.97)',
        transition: `opacity 0.6s ease ${index * 0.12}s, transform 0.6s ease ${index * 0.12}s`,
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-6px) scale(1.01)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 20px 60px ${t.greenGlow}`;
        (e.currentTarget as HTMLDivElement).style.borderColor = t.borderHover;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0) scale(1)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
        (e.currentTarget as HTMLDivElement).style.borderColor = t.border;
      }}
    >
      <div style={{ position: 'relative', height: 220, overflow: 'hidden', background: t.muted }}>
        <img
          src={img}
          alt={alt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.6s ease',
            display: 'block',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.06)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(to top, ${theme === 'dark' ? 'rgba(13,17,23,0.85)' : 'rgba(10,26,15,0.6)'} 0%, transparent 60%)`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            padding: '4px 12px',
            borderRadius: 50,
            background: t.green,
            color: theme === 'dark' ? '#0D1117' : '#fff',
            fontSize: 11,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          {label}
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            padding: '6px 14px',
            borderRadius: 10,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {stat}
        </div>
      </div>
      <div style={{ padding: '24px 24px 28px' }}>
        <h3
          style={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 800,
            fontSize: 18,
            color: t.fg,
            marginBottom: 10,
            lineHeight: 1.3,
          }}
        >
          {title}
        </h3>
        <p style={{ color: t.mutedFg, fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>{desc}</p>
        {/* <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            color: t.green,
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Learn more <ChevronRight size={14} />
        </div> */}
      </div>
    </div>
  );
}

function UseCasesSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const { ref, visible } = useScrollReveal();

  return (
    <section id="use-cases" style={{ padding: '80px 24px', background: t.bg }} ref={ref}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          style={{
            textAlign: 'center',
            marginBottom: 56,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: 50,
              background: t.greenDim,
              border: `1px solid ${t.border}`,
              fontSize: 11,
              color: t.green,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 20,
            }}
          >
            Use Cases
          </span>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(2rem, 4vw, 3.2rem)',
              fontWeight: 800,
              color: t.fg,
              marginBottom: 14,
              letterSpacing: '-0.5px',
            }}
          >
            Built for Every Industry
          </h2>
          <p style={{ color: t.mutedFg, fontSize: 17, maxWidth: 500, margin: '0 auto' }}>
            From retail to finance, WhatsApp powers growth across 68+ countries.
          </p>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 24,
          }}
        >
          {USE_CASES.map((c, i) => (
            <UseCaseCard key={c.label} {...c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── animated photo strip ── */
const STRIP_PHOTOS = [
  {
    url: 'https://images.unsplash.com/photo-1592890288564-76628a30a657?w=400&h=500&fit=crop&auto=format',
    alt: 'Person using smartphone',
  },
  {
    url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=500&fit=crop&auto=format',
    alt: 'Team collaboration',
  },
  {
    url: 'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?w=400&h=500&fit=crop&auto=format',
    alt: 'Person holding smartphone',
  },
  {
    url: 'https://images.unsplash.com/photo-1605902711622-cfb43c4437b5?w=400&h=500&fit=crop&auto=format',
    alt: 'Mobile commerce',
  },
  {
    url: 'https://images.unsplash.com/photo-1641951820920-c90394aef512?w=400&h=500&fit=crop&auto=format',
    alt: 'Business team with laptops',
  },
  {
    url: 'https://images.unsplash.com/photo-1648091856362-62436bfb145a?w=400&h=500&fit=crop&auto=format',
    alt: 'Phone on wooden surface',
  },
];

function PhotoStripSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const doubled = [...STRIP_PHOTOS, ...STRIP_PHOTOS];

  return (
    <section style={{ padding: '72px 0', background: t.card, overflow: 'hidden' }}>
      <div style={{ textAlign: 'center', marginBottom: 40, padding: '0 24px' }}>
        <span
          style={{
            display: 'inline-block',
            padding: '6px 16px',
            borderRadius: 50,
            background: t.greenDim,
            border: `1px solid ${t.border}`,
            fontSize: 11,
            color: t.green,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: 16,
          }}
        >
          Real Impact
        </span>
        <h2
          style={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
            fontWeight: 800,
            color: t.fg,
            letterSpacing: '-0.5px',
          }}
        >
          Businesses Thriving on WhatsApp
        </h2>
      </div>

      {/* Scrolling photo strip */}
      <div style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            gap: 16,
            width: 'max-content',
            animation: 'photoStrip 35s linear infinite',
          }}
        >
          {doubled.map((p, i) => (
            <div
              key={i}
              style={{
                flexShrink: 0,
                width: 260,
                height: 340,
                borderRadius: 20,
                overflow: 'hidden',
                border: `1px solid ${t.border}`,
                background: t.muted,
                position: 'relative',
                transform: i % 2 === 0 ? 'translateY(0px)' : 'translateY(20px)',
                transition: 'transform 0.4s ease, box-shadow 0.4s ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform =
                  i % 2 === 0 ? 'translateY(-8px) scale(1.03)' : 'translateY(12px) scale(1.03)';
                (e.currentTarget as HTMLDivElement).style.boxShadow = `0 24px 60px ${t.greenGlow}`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform =
                  i % 2 === 0 ? 'translateY(0px)' : 'translateY(20px)';
                (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
              }}
            >
              <img
                src={p.url}
                alt={p.alt}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: `linear-gradient(to top, ${theme === 'dark' ? 'rgba(13,17,23,0.4)' : 'rgba(0,0,0,0.2)'} 0%, transparent 50%)`,
                  borderRadius: 20,
                }}
              />
            </div>
          ))}
        </div>
        {/* Edge fades */}
        <div
          style={{
            position: 'absolute',
            inset: '0 auto 0 0',
            width: 120,
            background: `linear-gradient(to right, ${t.card}, transparent)`,
            pointerEvents: 'none',
            zIndex: 10,
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: '0 0 0 auto',
            width: 120,
            background: `linear-gradient(to left, ${t.card}, transparent)`,
            pointerEvents: 'none',
            zIndex: 10,
          }}
        />
      </div>

      <style>{`@keyframes photoStrip { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </section>
  );
}

/* ── pricing ── */
const CURRENCY_SYMBOL: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

function priceLabel(plan: PublicPlan): string {
  const sym = CURRENCY_SYMBOL[plan.currency] ?? `${plan.currency} `;
  return `${sym}${new Intl.NumberFormat('en-IN').format(plan.price)}`;
}

interface PricingCard {
  key: string;
  name: string;
  price: string | null;
  period: string | null;
  desc: string;
  highlight: boolean;
  badge?: string;
  features: Array<{ text: string; included: boolean }>;
  cta: string;
}

function planToCard(plan: PublicPlan): PricingCard {
  const isFree = !plan.price || plan.price <= 0;
  return {
    key: plan.name,
    name: plan.displayName,
    price: isFree ? null : priceLabel(plan),
    period: isFree ? null : '/mo',
    desc: plan.description,
    highlight: plan.isPopular,
    features: plan.highlights.map((h) => ({ text: h, included: true })),
    cta: isFree ? 'Get started free' : `Start ${plan.displayName}`,
    ...(plan.isPopular ? { badge: 'Most Popular' } : {}),
  };
}

// ─── Feature comparison ("Compare all features") ────────────────────────────
// Driven entirely by the per-plan `features` the super-admin edits (served by
// GET /public/plans), so the table never drifts from what each tier actually
// sells. 0 on a numeric limit means unlimited.
const nfmt = new Intl.NumberFormat('en-IN');
function limitLabel(n: number): string {
  return n > 0 ? nfmt.format(n) : 'Unlimited';
}
function storageLabel(bytes: number): string {
  if (!bytes || bytes <= 0) return 'Unlimited';
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

type PlanFeatures = NonNullable<PublicPlan['features']>;
interface CompareRow {
  label: string;
  cell: (f: PlanFeatures) => { text?: string; yes?: boolean };
}
const COMPARE_GROUPS: Array<{ title: string; rows: CompareRow[] }> = [
  {
    title: 'Channels',
    rows: [
      { label: 'WhatsApp Business', cell: (f) => ({ yes: f.whatsappEnabled }) },
      { label: 'Instagram DM', cell: (f) => ({ yes: f.instagramEnabled }) },
    ],
  },
  {
    title: 'AI & Knowledge Base',
    rows: [
      { label: 'AI agents', cell: (f) => ({ text: limitLabel(f.agentsLimit) }) },
      { label: 'Answers from your knowledge base (RAG)', cell: (f) => ({ yes: f.enableRag }) },
      { label: 'Custom LLM configuration', cell: (f) => ({ yes: f.llmConfigEnabled }) },
      { label: 'AI tokens / month', cell: (f) => ({ text: f.aiTokensLimit > 0 ? formatTokensCompact(f.aiTokensLimit) : 'Unlimited' }) },
    ],
  },
  {
    title: 'Automation & Campaigns',
    rows: [
      { label: 'Campaigns & broadcasts', cell: (f) => ({ yes: f.campaignsEnabled }) },
      { label: 'Workflow automation', cell: (f) => ({ yes: f.workflowsEnabled }) },
    ],
  },
  {
    title: 'Developer & Integrations',
    rows: [
      { label: 'Developer API', cell: (f) => ({ yes: f.apiAccess }) },
      { label: 'Webhooks & CRM lead sync', cell: (f) => ({ yes: f.webhooksEnabled }) },
    ],
  },
  {
    title: 'Scale',
    rows: [
      { label: 'Contacts', cell: (f) => ({ text: limitLabel(f.contactsLimit) }) },
      { label: 'Messages / month', cell: (f) => ({ text: limitLabel(f.messagesLimit) }) },
      { label: 'Team seats', cell: (f) => ({ text: limitLabel(f.usersLimit) }) },
      { label: 'Storage', cell: (f) => ({ text: storageLabel(f.storageLimit) }) },
    ],
  },
  {
    title: 'Branding & Support',
    rows: [
      { label: 'Custom branding', cell: (f) => ({ yes: f.customBranding }) },
      { label: 'Priority support', cell: (f) => ({ yes: f.prioritySupport }) },
    ],
  },
];

function FeatureComparison({ plans, t, isDark }: { plans: PublicPlan[]; t: any; isDark: boolean }) {
  const cols = [...plans].filter((p) => p.features).sort((a, b) => a.sortOrder - b.sortOrder);
  if (cols.length === 0) return null; // older/cached payload without features → skip gracefully

  const gridCols = `minmax(170px, 1.6fr) repeat(${cols.length}, minmax(92px, 1fr))`;
  const yesCell = (
    <span
      style={{
        display: 'inline-flex',
        width: 20,
        height: 20,
        borderRadius: '50%',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark ? 'rgba(61,220,132,0.15)' : 'rgba(37,211,102,0.15)',
      }}
    >
      <Check size={12} style={{ color: t.green, strokeWidth: 3 }} />
    </span>
  );
  const noCell = <span style={{ color: t.mutedFg, opacity: 0.45, fontSize: 16 }}>—</span>;

  return (
    <div style={{ marginTop: 72 }}>
      <h3
        style={{
          textAlign: 'center',
          fontFamily: 'Manrope, sans-serif',
          fontSize: 'clamp(1.4rem, 3vw, 2.1rem)',
          fontWeight: 800,
          color: t.fg,
          letterSpacing: '-0.5px',
          marginBottom: 8,
        }}
      >
        Compare all features
      </h3>
      <p style={{ textAlign: 'center', color: t.mutedFg, fontSize: 15, marginBottom: 32 }}>
        Everything each plan includes, side by side.
      </p>

      <div
        style={{
          overflowX: 'auto',
          border: `1px solid ${t.border}`,
          borderRadius: 20,
          background: t.card,
        }}
      >
        <div style={{ minWidth: 520 }}>
          {/* header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              borderBottom: `1px solid ${t.border}`,
            }}
          >
            <div
              style={{
                padding: '18px 20px',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: t.mutedFg,
              }}
            >
              Features
            </div>
            {cols.map((p) => (
              <div
                key={p.name}
                style={{
                  padding: '18px 12px',
                  textAlign: 'center',
                  fontSize: 13,
                  fontWeight: 800,
                  color: p.isPopular ? t.green : t.fg,
                }}
              >
                {p.displayName}
              </div>
            ))}
          </div>

          {COMPARE_GROUPS.map((group) => (
            <div key={group.title}>
              <div
                style={{
                  padding: '12px 20px 8px',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: t.green,
                  background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
                }}
              >
                {group.title}
              </div>
              {group.rows.map((row) => (
                <div
                  key={row.label}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridCols,
                    borderTop: `1px solid ${t.border}`,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ padding: '13px 20px', fontSize: 13, color: t.fg, fontWeight: 500 }}>
                    {row.label}
                  </div>
                  {cols.map((p) => {
                    const c = row.cell(p.features as PlanFeatures);
                    return (
                      <div
                        key={p.name}
                        style={{
                          padding: '13px 12px',
                          textAlign: 'center',
                          fontSize: 13,
                          color: t.fg,
                          fontWeight: 600,
                        }}
                      >
                        {c.text !== undefined ? c.text : c.yes ? yesCell : noCell}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PricingSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const navigate = useNavigate();
  const { ref, visible } = useScrollReveal();
  const isDark = theme === 'dark';
  const { plans, loading, error } = usePublicPlans();

  // No plans (unseeded env or fetch error) → don't render an empty section.
  if (!loading && (error || plans.length === 0)) return null;

  const cards = [...plans].sort((a, b) => a.sortOrder - b.sortOrder).map(planToCard);
  const goContact = (planKey: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/contact?plan=${encodeURIComponent(planKey)}`);
  };

  return (
    <section
      id="pricing"
      ref={ref}
      style={{ padding: '100px 24px', background: t.bg, position: 'relative', overflow: 'hidden' }}
    >
      {/* background glow */}
      <div
        style={{
          position: 'absolute',
          top: '30%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 800,
          height: 600,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${isDark ? 'rgba(61,220,132,0.06)' : 'rgba(37,211,102,0.08)'} 0%, transparent 70%)`,
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        {/* header */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: 64,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: 50,
              background: t.greenDim,
              border: `1px solid ${t.border}`,
              fontSize: 11,
              color: t.green,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              marginBottom: 20,
            }}
          >
            Pricing
          </span>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(2.2rem, 4.5vw, 3.6rem)',
              fontWeight: 800,
              color: t.fg,
              letterSpacing: '-1px',
              marginBottom: 14,
              lineHeight: 1.1,
            }}
          >
            Simple, transparent pricing
          </h2>
          <p style={{ color: t.mutedFg, fontSize: 16, maxWidth: 480, margin: '0 auto' }}>
            Plans that scale with your business. Meta WhatsApp charges billed separately at{' '}
            <span style={{ color: t.green, fontWeight: 700 }}>0% markup</span>.
          </p>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  width: 240,
                  height: 420,
                  borderRadius: 24,
                  border: `1px solid ${t.border}`,
                  background: t.card,
                  opacity: 0.5,
                }}
              />
            ))}
          </div>
        ) : (
          <>
            {/* cards grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 20,
                alignItems: 'stretch',
              }}
            >
              {cards.map((plan, i) => {
                const isHighlight = plan.highlight;
                return (
                  <div
                    key={plan.key}
                    style={{
                      position: 'relative',
                      borderRadius: 24,
                      padding: isHighlight ? '2px' : '0',
                      background: isHighlight
                        ? `linear-gradient(135deg, ${t.green} 0%, ${isDark ? '#2aa860' : '#1a9e52'} 100%)`
                        : 'transparent',
                      opacity: visible ? 1 : 0,
                      transform: visible
                        ? isHighlight
                          ? 'scale(1.03)'
                          : 'scale(1)'
                        : 'translateY(32px) scale(0.97)',
                      transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`,
                    }}
                  >
                    {/* popular badge */}
                    {plan.badge && (
                      <div
                        style={{
                          position: 'absolute',
                          top: -14,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          padding: '4px 16px',
                          borderRadius: 50,
                          background: t.green,
                          color: isDark ? '#050C09' : '#fff',
                          fontSize: 11,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          whiteSpace: 'nowrap',
                          zIndex: 10,
                        }}
                      >
                        {plan.badge}
                      </div>
                    )}

                    <div
                      style={{
                        borderRadius: isHighlight ? 22 : 24,
                        border: isHighlight ? 'none' : `1px solid ${t.border}`,
                        background: isHighlight ? (isDark ? '#0F1E17' : '#FAFFFE') : t.card,
                        padding: '32px 28px 36px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'box-shadow 0.3s, border-color 0.3s',
                      }}
                      onMouseEnter={(e) => {
                        const el = e.currentTarget as HTMLDivElement;
                        el.style.boxShadow = isHighlight
                          ? `0 24px 60px ${isDark ? 'rgba(61,220,132,0.25)' : 'rgba(37,211,102,0.3)'}`
                          : `0 16px 48px ${isDark ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'}`;
                        if (!isHighlight) el.style.borderColor = t.borderHover;
                      }}
                      onMouseLeave={(e) => {
                        const el = e.currentTarget as HTMLDivElement;
                        el.style.boxShadow = 'none';
                        if (!isHighlight) el.style.borderColor = t.border;
                      }}
                    >
                      {/* plan name */}
                      <div style={{ marginBottom: 20 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            color: isHighlight ? t.green : t.mutedFg,
                            marginBottom: 10,
                          }}
                        >
                          {plan.name}
                        </div>
                        {/* price */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 4,
                            marginBottom: 10,
                          }}
                        >
                          {plan.price ? (
                            <>
                              <span
                                style={{
                                  fontFamily: 'Manrope, sans-serif',
                                  fontSize: 'clamp(2rem, 4vw, 2.8rem)',
                                  fontWeight: 800,
                                  color: t.fg,
                                  letterSpacing: '-1px',
                                  lineHeight: 1,
                                }}
                              >
                                {plan.price}
                              </span>
                              <span
                                style={{
                                  fontSize: 14,
                                  color: t.mutedFg,
                                  fontWeight: 500,
                                  marginBottom: 4,
                                }}
                              >
                                {plan.period}
                              </span>
                            </>
                          ) : (
                            <span
                              style={{
                                fontFamily: 'Manrope, sans-serif',
                                fontSize: 'clamp(2rem, 4vw, 2.8rem)',
                                fontWeight: 800,
                                color: t.green,
                                letterSpacing: '-1px',
                                lineHeight: 1,
                              }}
                            >
                              Free
                            </span>
                          )}
                        </div>
                        <p style={{ fontSize: 13, color: t.mutedFg, lineHeight: 1.6 }}>
                          {plan.desc}
                        </p>
                      </div>

                      {/* divider */}
                      <div
                        style={{
                          height: 1,
                          background: isHighlight
                            ? `rgba(${isDark ? '61,220,132' : '37,211,102'},0.2)`
                            : t.border,
                          marginBottom: 24,
                        }}
                      />

                      {/* features */}
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 11,
                          marginBottom: 32,
                        }}
                      >
                        {plan.features.map((f) => (
                          <div
                            key={f.text}
                            style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                          >
                            <div
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: isDark
                                  ? 'rgba(61,220,132,0.15)'
                                  : 'rgba(37,211,102,0.15)',
                              }}
                            >
                              <Check size={11} style={{ color: t.green, strokeWidth: 3 }} />
                            </div>
                            <span style={{ fontSize: 13, color: t.fg, fontWeight: 500 }}>
                              {f.text}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* CTA */}
                      <a
                        href={`/contact?plan=${encodeURIComponent(plan.key)}`}
                        onClick={goContact(plan.key)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          padding: '13px 20px',
                          borderRadius: 12,
                          fontWeight: 700,
                          fontSize: 14,
                          textDecoration: 'none',
                          fontFamily: 'Manrope, sans-serif',
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s, background 0.2s',
                          ...(isHighlight
                            ? {
                                background: t.green,
                                color: isDark ? '#050C09' : '#fff',
                                boxShadow: `0 6px 28px ${isDark ? 'rgba(61,220,132,0.4)' : 'rgba(37,211,102,0.45)'}`,
                              }
                            : {
                                background: t.greenDim,
                                color: t.green,
                                border: `1.5px solid ${isDark ? 'rgba(61,220,132,0.3)' : 'rgba(37,211,102,0.4)'}`,
                              }),
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLAnchorElement).style.transform =
                            'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
                        }}
                      >
                        {plan.cta} <ArrowRight size={14} />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Compare all features — tier-by-tier, from the live plan config. */}
            <FeatureComparison plans={plans} t={t} isDark={isDark} />

            {/* footnote */}
            <p
              style={{
                textAlign: 'center',
                marginTop: 36,
                fontSize: 13,
                color: t.mutedFg,
                opacity: 0.75,
              }}
            >
              All plans include a <strong style={{ color: t.fg }}>14-day free trial</strong>. No
              credit card required. Cancel anytime.
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function GetStartedSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const navigate = useNavigate();
  const goSignup = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/signup');
  };

  return (
    <section style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          style={{
            position: 'relative',
            borderRadius: 28,
            overflow: 'hidden',
            padding: '80px 48px',
            textAlign: 'center',
            background:
              theme === 'dark'
                ? 'linear-gradient(135deg, #0D2318 0%, #06090A 50%, #0D2318 100%)'
                : 'linear-gradient(135deg, #D9F2E4 0%, #F4FFF8 50%, #D9F2E4 100%)',
            border: `1px solid ${t.border}`,
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(ellipse at 50% 0%, ${t.greenGlow} 0%, transparent 60%)`,
              opacity: 0.5,
            }}
          />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 18px',
                borderRadius: 50,
                background: t.greenDim,
                border: `1px solid ${t.border}`,
                color: t.green,
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 28,
              }}
            >
              <Clock size={14} /> Get live in under 10 minutes
            </div>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontSize: 'clamp(2rem, 5vw, 3.8rem)',
                fontWeight: 800,
                color: t.fg,
                marginBottom: 20,
                letterSpacing: '-1px',
                lineHeight: 1.1,
              }}
            >
              Start WhatsApp Marketing
              <br />
              <span style={{ color: t.green }}>in 10 Minutes</span>
            </h2>
            <p style={{ color: t.mutedFg, fontSize: 18, maxWidth: 520, margin: '0 auto 36px' }}>
              No credit card required. Connect your number, import contacts, and send your first
              broadcast — that fast.
            </p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 16,
                marginBottom: 28,
              }}
            >
              <a
                href="/signup"
                onClick={goSignup}
                style={{
                  padding: '16px 32px',
                  borderRadius: 14,
                  background: t.green,
                  color: theme === 'dark' ? '#06090A' : '#fff',
                  fontWeight: 800,
                  fontSize: 16,
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  boxShadow: `0 0 50px ${t.greenGlow}`,
                  cursor: 'pointer',
                }}
              >
                Start FREE Trial Now
                <ArrowRight size={17} />
              </a>
              {/* <a
                href="#"
                style={{
                  padding: '16px 32px',
                  borderRadius: 14,
                  border: `1.5px solid ${t.border}`,
                  color: t.fg,
                  fontWeight: 700,
                  fontSize: 16,
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: t.greenDim,
                }}
              >
                <Play size={15} style={{ fill: 'currentColor' }} /> Join Live Demo
              </a> */}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24 }}>
              {[
                'Official WhatsApp API',
                'No credit card needed',
                '24/7 support',
                'Setup in 10 mins',
              ].map((f) => (
                <div
                  key={f}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    color: t.mutedFg,
                    fontSize: 13,
                  }}
                >
                  <Check size={13} color={t.green} strokeWidth={3} /> {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section id="faq" style={{ padding: '80px 24px', background: t.card }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              color: t.fg,
              marginBottom: 12,
              letterSpacing: '-0.5px',
            }}
          >
            FAQ
          </h2>
          <p style={{ color: t.mutedFg, fontSize: 17 }}>
            Everything you need to know about WhatsApp.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FAQS.map(({ q, a }, i) => (
            <div
              key={i}
              style={{
                background: t.bg,
                border: `1px solid ${open === i ? t.borderHover : t.border}`,
                borderRadius: 16,
                overflow: 'hidden',
                transition: 'border-color 0.2s',
              }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  padding: '18px 22px',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    color: t.fg,
                    fontSize: 15,
                  }}
                >
                  {q}
                </span>
                <ChevronDown
                  size={18}
                  color={t.green}
                  style={{
                    flexShrink: 0,
                    transform: open === i ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.3s',
                  }}
                />
              </button>
              {open === i && (
                <div style={{ padding: '0 22px 20px' }}>
                  <p style={{ color: t.mutedFg, fontSize: 14, lineHeight: 1.75, margin: 0 }}>{a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── root ── */
export default function Home() {
  const themeCtxValue = useThemeState('light');
  const t = THEMES[themeCtxValue.theme];
  const location = useLocation();

  // Nav/footer "scroll to section" links from other pages land here as
  // navigate('/#pricing') etc. — finish the job once that section has mounted.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const timer = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <ThemeCtx.Provider value={themeCtxValue}>
      <div
        style={{
          minHeight: '100vh',
          background: t.bg,
          color: t.fg,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <NavBar />
        <main>
          <HeroSection />
          <UseCasesSection />
          <PhotoStripSection />
          <WhyWhatsAppSection />
          <FeaturesTabSection />
          <PlatformFeaturesSection />
          <BroadcastAnalyticsSection />
          <FeaturesGrid />
          <TestimonialsSection />
          <PricingSection />
          <GetStartedSection />
          <FAQSection />
        </main>
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}
